import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  padding-top: 8rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Card = styled.div`
  width: 100%;
  width: 400px;
  max-width: 850px;
  background: white;
  border-radius: 8px;
  box-shadow: 1px 2px 15px -5px rgb(0 0 0 / 1.4);
`;

const CardHeader = styled.div`
  padding: 24px;
  text-align: center;
`;

const CardTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const CardDescription = styled.p`
  color: #666;
  font-size: 14px;
`;

const Form = styled.form`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  width: calc(100% - 16px);
`;

const Button = styled.button`
  width: 100%;
  padding: 8px;
  background: #000;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;

  &:disabled {
    opacity: 0.7;
  }
`;

const Message = styled.p`
  text-align: center;
  font-size: 14px;
  color: ${props => props.error ? 'red' : '#28a745'};
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const Step = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.active ? '#000' : '#e2e8f0'};
`;

function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [account, setAccount] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSendCode = async () => {
    if (!account) {
      setMessage('계정을 입력해주세요.');
      setIsError(true);
      return;
    }
    setIsLoading(true);
    setMessage('');
    try {
      const response = await fetch('/request/forgot-password/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_account: account })
      });
      const data = await response.json();
      if (data.success) {
        setStep(2);
        setMessage('인증번호가 전송되었습니다.');
        setIsError(false);
      } else {
        setMessage(data.message || '전송에 실패했습니다.');
        setIsError(true);
      }
    } catch (error) {
      setMessage('서버 연결에 실패했습니다.');
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode) {
      setMessage('인증번호를 입력해주세요.');
      setIsError(true);
      return;
    }
    setIsLoading(true);
    setMessage('');
    try {
      const response = await fetch('/request/forgot-password/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_account: account, code: verificationCode })
      });
      const data = await response.json();
      if (data.success) {
        setResetToken(data.reset_token);
        setStep(3);
        setMessage('인증 성공! 새 비밀번호를 입력해주세요.');
        setIsError(false);
      } else {
        setMessage(data.message || '인증번호가 올바르지 않습니다.');
        setIsError(true);
      }
    } catch (error) {
      setMessage('서버 연결에 실패했습니다.');
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      setMessage('비밀번호를 입력해주세요.');
      setIsError(true);
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage('비밀번호가 일치하지 않습니다.');
      setIsError(true);
      return;
    }
    if (newPassword.length < 4) {
      setMessage('비밀번호는 4자 이상이어야 합니다.');
      setIsError(true);
      return;
    }
    setIsLoading(true);
    setMessage('');
    try {
      const response = await fetch('/request/forgot-password/reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reset_token: resetToken, new_password: newPassword })
      });
      const data = await response.json();
      if (data.success) {
        setMessage('비밀번호가 변경되었습니다. 로그인 페이지로 이동합니다.');
        setIsError(false);
        setTimeout(() => navigate('/Login'), 2000);
      } else {
        setMessage(data.message || '비밀번호 변경에 실패했습니다.');
        setIsError(true);
      }
    } catch (error) {
      setMessage('서버 연결에 실패했습니다.');
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <Card>
        <CardHeader>
          <CardTitle>비밀번호 찾기</CardTitle>
          <CardDescription>
            {step === 1 && '계정을 입력하면 인증번호가 전송됩니다.'}
            {step === 2 && '전송된 인증번호를 입력해주세요.'}
            {step === 3 && '새 비밀번호를 설정해주세요.'}
          </CardDescription>
          <StepIndicator>
            <Step active={step >= 1} />
            <Step active={step >= 2} />
            <Step active={step >= 3} />
          </StepIndicator>
        </CardHeader>
        <Form onSubmit={(e) => {
          e.preventDefault();
          if (step === 1) handleSendCode();
          else if (step === 2) handleVerifyCode();
          else handleResetPassword();
        }}>
          {message && <Message error={isError}>{message}</Message>}

          {step === 1 && (
            <InputGroup>
              <Label>계정 (아이디)</Label>
              <Input
                type="text"
                value={account}
                onChange={(e) => setAccount(e.target.value)}
                placeholder="아이디를 입력하세요"
                required
              />
            </InputGroup>
          )}

          {step === 2 && (
            <InputGroup>
              <Label>인증번호</Label>
              <Input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="인증번호 입력"
                required
              />
            </InputGroup>
          )}

          {step === 3 && (
            <>
              <InputGroup>
                <Label>새 비밀번호</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="새 비밀번호"
                  required
                />
              </InputGroup>
              <InputGroup>
                <Label>비밀번호 확인</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="비밀번호 확인"
                  required
                />
              </InputGroup>
            </>
          )}

          <Button type="submit" disabled={isLoading}>
            {isLoading ? '처리중...' :
              step === 1 ? '인증번호 전송' :
              step === 2 ? '인증번호 확인' : '비밀번호 변경'}
          </Button>

          <Link to="/Login" style={{textAlign: 'center', color: '#666', textDecoration: 'none'}}>
            로그인으로 돌아가기
          </Link>
        </Form>
      </Card>
    </Container>
  );
}

export default ForgotPassword;
